import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, ArrowRight, CheckCircle2, Heart, Star, MessageCircle, Users, Globe, Award, Compass, Handshake } from "lucide-react";

const SECTORS = [
  "Schools & School Districts",
  "Colleges & Universities",
  "Workforce Development",
  "Community Organizations",
  "Labor & Union Partners",
  "Sports Organizations",
  "Employers & Businesses",
  "Global Partners",
];

const domains = [
  { icon: Heart, name: "Emotional Intelligence", color: "#EC4899" },
  { icon: Star, name: "Leadership", color: "#C9A961" },
  { icon: MessageCircle, name: "Communication", color: "#0B1E36" },
  { icon: Users, name: "Belonging", color: "#2C7A7B" },
  { icon: Globe, name: "Community Engagement", color: "#8B5CF6" },
];

const partnerBenefits = [
  { icon: Award, title: "Founding Partner Recognition", desc: "Be named among the first EQ Partners driving the movement in your sector." },
  { icon: Compass, title: "Full Access to Future Counts OS™", desc: "The platform, the Future Counts Index™, and every learning experience across all five domains." },
  { icon: Handshake, title: "Implementation Support", desc: "Guidance from the Collective on rolling out human-development initiatives in your organization." },
  { icon: Users, title: "A National Community", desc: "Join a cross-sector community of leaders committed to developing the human side of success." },
];

export default function Partners() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", sector: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/partners/join", form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch shortly.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] grain-bg">
      <header className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="partners-brand-link">
          <div className="h-10 w-10 rounded-xl bg-[#0B1E36] grid place-items-center">
            <Sparkles className="h-5 w-5 text-[#C9A961]" />
          </div>
          <div>
            <div className="font-serif text-xl leading-tight text-[#0B1E36]">Future Counts OS™</div>
            <div className="text-[10px] tracking-widest text-slate-500 uppercase">by the Future Counts Collective</div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="partners-back" className="text-sm font-medium text-slate-600 hover:text-[#0B1E36] inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Back home
          </Link>
          <Link to="/about" data-testid="partners-to-about" className="text-sm font-medium text-slate-600 hover:text-[#0B1E36] px-4 py-2">About</Link>
        </div>
      </header>

      {submitted ? (
        <section className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center fade-up" data-testid="partners-success">
          <div className="h-16 w-16 mx-auto rounded-full bg-[#EC4899] grid place-items-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-white" />
          </div>
          <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">Application received</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-[#0B1E36] leading-tight mb-4">Welcome to the movement.</h1>
          <p className="text-slate-600 leading-relaxed max-w-xl mx-auto">
            Thank you for stepping forward as an EQ Partner. Karen and the Collective team will personally review your application and reach out within the next few business days with next steps.
          </p>
          <blockquote className="mt-12 font-serif text-2xl italic text-[#0B1E36] leading-snug max-w-xl mx-auto">
            "Who individuals are becoming matters as much as what they know."
          </blockquote>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-3">— Karen Pinckney, Founder</div>
          <div className="mt-14 flex justify-center gap-4">
            <Link to="/" data-testid="success-home" className="bg-[#0B1E36] text-white px-6 py-3 rounded-full font-semibold btn-lift hover:bg-[#132a4a]">Back home</Link>
            <Link to="/about" data-testid="success-about" className="bg-white border border-slate-200 text-[#0B1E36] px-6 py-3 rounded-full font-semibold btn-lift hover:bg-slate-50">Read our story</Link>
          </div>
        </section>
      ) : (
        <>
          {/* ── Hero ─────────────────────────────── */}
          <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-12 pb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C9A961]/15 text-[#6B5228] text-xs font-semibold tracking-wide mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C9A961]" /> A PROGRAM OF THE EQ INITIATIVE™ · FOUNDING COHORT
            </div>
            <h1 className="font-serif text-5xl lg:text-6xl text-[#0B1E36] leading-[1.05] tracking-tight max-w-4xl">
              The <span className="italic">EQ Partners Campaign™</span> — a movement for the human side of success.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              The EQ Partners Campaign™ is the partnership-building program of the EQ Initiative™ — advancing human development, emotional intelligence, leadership, communication, belonging, and community engagement across sectors.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#join" data-testid="hero-join" className="inline-flex items-center gap-2 bg-[#EC4899] text-white px-6 py-3.5 rounded-full font-semibold btn-lift hover:bg-[#DB2777] shadow-[0_8px_24px_rgba(236,72,153,0.25)]">
                Apply to become an EQ Partner <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/about" data-testid="hero-learn" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1E36] hover:underline underline-offset-4 py-3.5">
                Read our full story
              </Link>
            </div>
          </section>

          {/* ── What is it ────────────────────────── */}
          <section className="border-t border-slate-200 bg-white">
            <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-3 gap-12">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">The Initiative</div>
                <h2 className="font-serif text-3xl text-[#0B1E36] leading-tight">What the EQ Partners Campaign™ is.</h2>
              </div>
              <div className="lg:col-span-2 space-y-5 text-[15px] text-slate-700 leading-[1.75]">
                <p>The EQ Partners Campaign™ is the flagship initiative of the Future Counts Collective. It brings together forward-thinking schools, universities, employers, community organizations, labor partners, sports organizations, and workforce leaders around a shared commitment: to intentionally develop the human skills that support long-term success and well-being.</p>
                <p>Founding EQ Partners receive full access to <strong className="text-[#0B1E36]">Future Counts OS™</strong> — the platform, the <strong className="text-[#0B1E36]">Future Counts Index™</strong> measurement, and the learning experiences that operationalize the five domains of human development.</p>
                <p>This isn't another training program. It's an operating system for how your organization develops emotional intelligence, leadership, communication, belonging, and community engagement — measurably, over time.</p>
              </div>
            </div>
          </section>

          {/* ── 5 Domains ────────────────────────── */}
          <section className="bg-[#FDFBF7] border-t border-slate-200">
            <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
              <div className="text-center max-w-2xl mx-auto mb-10">
                <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">The Framework</div>
                <h2 className="font-serif text-3xl lg:text-4xl text-[#0B1E36] leading-tight">Partners grow across five core domains.</h2>
              </div>
              <div className="grid md:grid-cols-5 gap-4">
                {domains.map((d) => {
                  const Icon = d.icon;
                  return (
                    <div key={d.name} className="bg-white p-5 rounded-2xl border border-slate-200 text-center">
                      <div className="h-11 w-11 rounded-xl mx-auto grid place-items-center mb-3" style={{ background: d.color + "15" }}>
                        <Icon className="h-5 w-5" style={{ color: d.color }} />
                      </div>
                      <div className="font-serif text-sm text-[#0B1E36] leading-tight">{d.name}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ── What partners get ────────────────── */}
          <section className="bg-white border-t border-slate-200">
            <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
              <div className="text-center max-w-2xl mx-auto mb-14">
                <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">What Partners Receive</div>
                <h2 className="font-serif text-3xl lg:text-4xl text-[#0B1E36] leading-tight">Real infrastructure, real community, real recognition.</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {partnerBenefits.map((b) => {
                  const Icon = b.icon;
                  return (
                    <div key={b.title} className="p-8 rounded-2xl border border-slate-200 bg-[#FDFBF7] card-lift" data-testid={`benefit-${b.title.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                      <div className="flex items-center gap-4 mb-3">
                        <div className="h-11 w-11 rounded-xl bg-[#0B1E36] grid place-items-center">
                          <Icon className="h-5 w-5 text-[#C9A961]" />
                        </div>
                        <div className="font-serif text-lg text-[#0B1E36] leading-tight">{b.title}</div>
                      </div>
                      <p className="text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ── Join form ────────────────────────── */}
          <section id="join" className="border-t border-slate-200 bg-[#0B1E36] text-white scroll-mt-16">
            <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
              <div className="text-center mb-12">
                <div className="text-xs uppercase tracking-widest text-[#C9A961] font-semibold mb-3">Apply to Join</div>
                <h2 className="font-serif text-4xl lg:text-5xl leading-[1.1] mb-4">Become a founding EQ Partner.</h2>
                <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto">
                  Complete the short form below. Karen and the Collective team personally review every application and will reach out within the next few business days.
                </p>
              </div>

              <form onSubmit={submit} className="bg-white text-[#0B1E36] p-8 lg:p-10 rounded-3xl shadow-[0_30px_80px_rgba(11,30,54,0.25)]">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Full name</label>
                    <input required value={form.name} onChange={upd("name")} data-testid="partner-name"
                      className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Organization</label>
                    <input required value={form.organization} onChange={upd("organization")} data-testid="partner-org" placeholder="e.g. Lincoln USD"
                      className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Email address</label>
                    <input required type="email" value={form.email} onChange={upd("email")} data-testid="partner-email"
                      className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Phone number</label>
                    <input required type="tel" value={form.phone} onChange={upd("phone")} data-testid="partner-phone" placeholder="(555) 123-4567"
                      className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Your sector</label>
                    <select required value={form.sector} onChange={upd("sector")} data-testid="partner-sector"
                      className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none bg-white">
                      <option value="">Select your sector…</option>
                      {SECTORS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Anything you'd like us to know? <span className="normal-case font-normal text-slate-400">(optional)</span></label>
                    <textarea rows={4} value={form.message} onChange={upd("message")} data-testid="partner-message"
                      placeholder="Tell us about your organization's current focus, or why the EQ Partners Campaign resonates with you."
                      className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#EC4899]/40 focus:border-[#EC4899] outline-none resize-none" />
                  </div>
                </div>
                <button
                  type="submit" disabled={loading} data-testid="partner-submit"
                  className="mt-8 w-full bg-[#EC4899] text-white py-4 rounded-full font-semibold text-base btn-lift hover:bg-[#DB2777] disabled:opacity-60 shadow-[0_8px_24px_rgba(236,72,153,0.25)]">
                  {loading ? "Submitting…" : "Apply to become an EQ Partner"}
                </button>
                <div className="mt-4 text-center text-xs text-slate-500">
                  By applying, you agree to be contacted by the Future Counts Collective team about your application.
                </div>
              </form>

              <div className="text-center mt-14 pt-10 border-t border-white/10">
                <blockquote className="font-serif text-2xl italic leading-snug max-w-2xl mx-auto text-slate-200">
                  "Who individuals are becoming matters as much as what they know."
                </blockquote>
                <div className="text-xs uppercase tracking-widest text-[#C9A961] mt-3 font-semibold">— Karen Pinckney, Founder</div>
              </div>
            </div>
          </section>
        </>
      )}

      <footer className="border-t border-slate-200 py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-xs text-slate-500">
          <div>© 2026 Future Counts Collective</div>
          <div>Building a better future — together.</div>
        </div>
      </footer>
    </div>
  );
}
